"use client";
import { a as ToastClose, c as ToastContent, d as ToastProvider$1, i as ToastPortal, l as ToastRoot, n as createToastManager, o as ToastTitle, r as useToastManager, s as ToastDescription, t as index_parts_exports, u as ToastViewport } from "../chunks/vendor-base-ui-n0ony04mhklkmnfd.js";
import { t as cn } from "../chunks/cn-dk9nmc4mhy1765ud.js";
import { Button } from "./button.js";
import { n as usePortalContainer } from "../chunks/portal-provider-mv0x5fh2psxbmcl8.js";
import { Box, Col, Row } from "./box.js";
import { jsx, jsxs } from "react/jsx-runtime";
import IconX from "@tabler/icons-react/dist/esm/icons/IconX.mjs";
import IconAlertOctagonFilled from "@tabler/icons-react/dist/esm/icons/IconAlertOctagonFilled.mjs";
import IconAlertTriangleFilled from "@tabler/icons-react/dist/esm/icons/IconAlertTriangleFilled.mjs";
import IconCircleCheckFilled from "@tabler/icons-react/dist/esm/icons/IconCircleCheckFilled.mjs";
import IconInfoCircleFilled from "@tabler/icons-react/dist/esm/icons/IconInfoCircleFilled.mjs";
//#region src/components/toast/toast.tsx
/**
* Toast styling configuration for Figma plugin consumption.
* Toast has no user-facing variants but documents the styling structure.
*/
var ATOMS_TOAST_VARIANTS = {
	root: {
		classes: "rounded-xl ring ring-od-border-tertiary bg-od-bg-inverse-primary p-4 shadow-lg text-od-content-inverse-primary",
		description: "Toast container with inverse background, ring, and shadow"
	},
	title: {
		classes: "text-label-md text-od-content-inverse-primary",
		description: "Toast title with primary text color"
	},
	description: {
		classes: "text-body-sm text-od-content-inverse-secondary",
		description: "Toast description with muted text color"
	},
	close: {
		classes: "absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded bg-transparent text-od-content-inverse-secondary hover:bg-od-bg-inverse-hover-primary hover:text-od-content-inverse-primary",
		description: "Close button with X icon"
	},
	variant: {
		default: {
			classes: "ring-od-border-tertiary bg-od-bg-inverse-primary",
			description: "Default toast style"
		},
		success: {
			classes: "ring-[0.3px] ring-od-border-success bg-od-bg-success [&_[data-toast-icon]]:text-od-content-inverse-primary [&_[data-toast-title]]:text-od-content-inverse-primary",
			description: "Success toast for confirmations and positive outcomes",
			icon: IconCircleCheckFilled
		},
		error: {
			classes: "ring-[0.3px] ring-od-border-error bg-od-bg-error [&_[data-toast-icon]]:text-od-content-inverse-primary [&_[data-toast-title]]:text-od-content-inverse-primary",
			description: "Error toast for critical issues",
			icon: IconAlertOctagonFilled
		},
		warning: {
			classes: "ring-[0.3px] ring-od-border-error bg-od-bg-error [&_[data-toast-icon]]:text-od-content-inverse-primary [&_[data-toast-title]]:text-od-content-inverse-primary",
			description: "Warning toast for cautionary messages",
			icon: IconAlertTriangleFilled
		},
		info: {
			classes: "ring-[0.3px] ring-od-border-accent-brand bg-od-bg-accent-brand [&_[data-toast-icon]]:text-od-content-inverse-primary [&_[data-toast-title]]:text-od-content-inverse-primary",
			description: "Info toast for neutral informational messages",
			icon: IconInfoCircleFilled
		}
	}
};
var ATOMS_TOAST_DEFAULT_VARIANTS = { variant: "default" };
/**
* Toast styling configuration for Figma plugin consumption.
* Provides structured metadata for generating Toast components in Figma.
*/
var ATOMS_TOAST_STYLING = {
	container: {
		width: 340,
		padding: 16,
		borderRadius: 12,
		background: "bg-od-bg-inverse-primary",
		border: "ring ring-od-border-tertiary",
		shadow: "shadow-lg",
		gap: 4
	},
	title: {
		fontSize: 16,
		fontWeight: 500,
		color: "text-od-content-inverse-primary"
	},
	description: {
		fontSize: 14,
		fontWeight: 400,
		color: "text-od-content-inverse-secondary"
	},
	icons: [
		{
			name: "tabler-circle-check-filled",
			state: "success",
			size: 16
		},
		{
			name: "tabler-alert-octagon-filled",
			state: "error",
			size: 16
		},
		{
			name: "tabler-alert-triangle-filled",
			state: "warning",
			size: 16
		},
		{
			name: "tabler-info-circle-filled",
			state: "info",
			size: 16
		}
	],
	closeButton: {
		size: 20,
		iconSize: 16,
		iconName: "tabler-x",
		iconColor: "text-od-content-inverse-secondary",
		hoverBackground: "bg-od-bg-inverse-hover-primary",
		hoverColor: "text-od-content-inverse-primary",
		borderRadius: 4
	}
};
function toastVariants({ variant = ATOMS_TOAST_DEFAULT_VARIANTS.variant } = {}) {
	return cn("rounded-xl ring ring-od-border-tertiary bg-clip-padding p-4 shadow-lg text-od-content-inverse-primary", ATOMS_TOAST_VARIANTS.variant[variant].classes);
}
function wrapManagerMethods(manager) {
	return {
		...manager,
		add: (options) => {
			const { id } = options;
			if (id) {
				const toasts = manager.toasts;
				if (toasts) {
					const existingToast = toasts.find((toast) => toast.id === id);
					if (existingToast && existingToast.transitionStatus !== "ending") {
						manager.update(id, { bump: false });
						requestAnimationFrame(() => {
							manager.update(id, {
								bump: true,
								...options.timeout !== void 0 && { timeout: options.timeout }
							});
						});
						return id;
					}
					if (existingToast && existingToast.transitionStatus === "ending") return id;
				}
			}
			return manager.add({ ...options });
		},
		update: (id, options) => {
			return manager.update(id, { ...options });
		},
		promise: (promise, options) => {
			const { loading, success, error } = options;
			return manager.promise(promise, {
				loading: { ...loading },
				success: typeof success === "function" ? (data) => ({ ...success(data) }) : { ...success },
				error: typeof error === "function" ? (errorArg) => ({ ...error(errorArg) }) : { ...error }
			});
		}
	};
}
var useAtomsToastManager = () => {
	const manager = useToastManager();
	const toasts = manager.toasts;
	return {
		...wrapManagerMethods(manager),
		toasts
	};
};
var createAtomsToastManager = () => {
	return wrapManagerMethods(createToastManager());
};
/**
* Toasty — toast notification provider and viewport.
*
* Renders a `Toast.Provider` with a fixed-position viewport at the bottom right.
* Toasts stack with smooth enter/exit animations, swipe-to-dismiss, and expand-on-hover.
*
* Built on `@base-ui/react/toast`.
*
* @example
* ```tsx
* <Toasty>
*   <App />
* </Toasty>
* ```
*/
function Toasty({ children, container: containerProp }) {
	const contextContainer = usePortalContainer();
	return /* @__PURE__ */ jsxs(ToastProvider$1, { children: [children, /* @__PURE__ */ jsx(ToastPortal, {
		container: containerProp ?? contextContainer ?? void 0,
		children: /* @__PURE__ */ jsx(ToastViewport, {
			className: "fixed top-auto right-4 bottom-4 z-1 flex w-[calc(100%-2rem)] sm:right-8 sm:bottom-8 sm:w-85",
			children: /* @__PURE__ */ jsx(ToastList, {})
		})
	})] });
}
/** Alias for Toasty — provided for discoverability when migrating from other libraries */
var ToastProvider = Toasty;
function ToastList() {
	const { toasts } = useAtomsToastManager();
	return toasts.map((toast) => /* @__PURE__ */ jsxs(ToastRoot, {
		toast,
		className: cn("absolute right-0 bottom-0 left-auto z-[calc(1000-var(--toast-index))] h-(--height) w-full origin-bottom select-none", toastVariants({ variant: toast.variant }), "[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(calc(var(--toast-offset-y)*-1)-calc(var(--toast-index)*var(--gap))+var(--toast-swipe-movement-y))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]", "transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]", "after:absolute after:bottom-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']", "data-ending-style:opacity-0 data-expanded:h-(--toast-height) data-expanded:transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--offset-y)))] data-limited:opacity-0 data-starting-style:transform-[translateY(150%)]", "data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+150%))] data-expanded:data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+150%))]", "data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))] data-expanded:data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]", "data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))] data-expanded:data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]", "data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))] data-expanded:data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))]", "[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:transform-[translateY(150%)]", toast.bump && "animate-toast-bump"),
		children: [/* @__PURE__ */ jsx(ToastBackground, { variant: toast.variant }), /* @__PURE__ */ jsxs(ToastContent, {
			className: "isolate flex flex-col gap-1 transition-opacity duration-250 data-behind:pointer-events-none data-behind:opacity-0 data-expanded:pointer-events-auto data-expanded:opacity-100",
			children: [toast.content ?? /* @__PURE__ */ jsxs(Row, {
				className: "items-start gap-2",
				children: [/* @__PURE__ */ jsx(ToastIcon, { variant: toast.variant }), /* @__PURE__ */ jsxs(Col, {
					className: "gap-1 overflow-hidden",
					children: [
						/* @__PURE__ */ jsx(ToastTitle, {
							"data-toast-title": true,
							className: "text-label-md text-od-content-inverse-primary"
						}),
						/* @__PURE__ */ jsx(ToastDescription, { className: "text-body-sm text-od-content-inverse-secondary" }),
						!!toast.actions && /* @__PURE__ */ jsx(Row, {
							className: "min-w-0 flex-nowrap gap-2 overflow-x-auto px-px pt-2 pb-px",
							children: toast.actions.map((actionProps, idx) => /* @__PURE__ */ jsx(Button, { ...actionProps }, idx))
						})
					]
				})]
			}), /* @__PURE__ */ jsx(ToastClose, {
				className: "absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded border-none bg-transparent text-od-content-inverse-secondary hover:bg-od-bg-inverse-hover-primary hover:text-od-content-inverse-primary",
				"aria-label": "Close",
				children: /* @__PURE__ */ jsx(IconX, { className: "h-4 w-4" })
			})]
		})]
	}, toast.id));
}
var TOAST_BACKGROUND_CLASSES = {
	success: "bg-od-bg-success/5",
	error: "bg-od-bg-error/5",
	warning: "bg-od-bg-error/5",
	info: "bg-od-bg-accent-brand/5"
};
function ToastBackground({ variant }) {
	return /* @__PURE__ */ jsx(Box, { className: cn("absolute inset-0 rounded-xl bg-od-bg-inverse-primary/90", variant && TOAST_BACKGROUND_CLASSES[variant]) });
}
function ToastIcon({ variant }) {
	if (!variant || variant === "default") return null;
	const variantConfig = ATOMS_TOAST_VARIANTS.variant[variant];
	if (!("icon" in variantConfig)) return null;
	const Icon = variantConfig.icon;
	return /* @__PURE__ */ jsx(Icon, {
		"data-toast-icon": true,
		className: "translate-y-0.5 h-4 w-4 shrink-0"
	});
}
//#endregion
export { ATOMS_TOAST_DEFAULT_VARIANTS, ATOMS_TOAST_STYLING, ATOMS_TOAST_VARIANTS, index_parts_exports as Toast, ToastProvider, Toasty, createAtomsToastManager, toastVariants, useAtomsToastManager };

